import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Divider, Row, Dropdown, message, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import PersonalInfoEdit from '../popups/personalInfoEdit/PersonalInfoEdit';
import EmailEdit from '../popups/emailEdit/EmailEdit';
import AsiaMilesEdit from '../popups/asiaMilesEdit/AsiaMilesEdit';
import HomeAddressEdit from '../popups/homeAddressEdit/HomeAddressEdit';
import './PersonalInfoSection.scss';
import AddressEdit, { AddressEditMode, } from '../popups/addressEdit/AddressEdit';
import { fetchAddress } from 'services/orderService';
import { AddressItem } from 'types/dto/order';
import { LoyaltyAccount } from 'types/dto/profile';
import NewAddress from 'pages/order/newOrder/subviews/addressManagement/NewAddress';
import DrawerFormContainer from 'components/drawerFormContainer/DrawerFormContainer';
import {
  convertToOptions,
  getMonthNameByNumber,
  maskEmail,
} from 'utils/commonUtils';
import editIcon from 'assets/images/edit.png';
import smallPlusIcon from 'assets/images/small_plus.png';
import { CommonOption } from 'types/common';
import { fetchTitle } from 'services/profileService';
import { useSession } from 'context/SessionContext';

export interface PersonalInfoSectionProps {
  loyaltyAccount?: LoyaltyAccount;
  onSaveCallback?: () => void;
}

const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  loyaltyAccount,
  onSaveCallback,
}) => {
  const { t, i18n } = useTranslation('profile');
  const { t: tCommon } = useTranslation('common');
  const lang = i18n.language;
  const isEnglish = lang === 'en';

  // State for drawer visibility
  const [isPersonalInfoEditVisible, setIsPersonalInfoEditVisible] =
    useState(false);
  const [isEmailEditVisible, setIsEmailEditVisible] = useState(false);
  const [isAsiaMilesEditVisible, setIsAsiaMilesEditVisible] = useState(false);
  const [isHomeAddressEditVisible, setIsHomeAddressEditVisible] = useState(false);
  const [isAddressDropdownVisible, setIsAddressDropdownVisible] =
    useState(false);
  const [isMobileAddressDropdownVisible, setIsMobileAddressDropdownVisible] =
    useState(false);
  const [isAddressEditVisible, setIsAddressEditVisible] = useState(false);
  const [editAddressMode, setEditAddressMode] =
    useState<AddressEditMode>('delete');

  const [addresses, setAddresses] = useState<AddressItem[]>([]);

  const [isNewAddressVisible, setIsNewAddressVisible] = useState(false);

  const [titleOptions, setTitleOptions] = useState<CommonOption[]>();

  const { session } = useSession();

  const hasEmail = loyaltyAccount?.email && loyaltyAccount?.email !== '';
  const hasAsiaMilesNo =
    loyaltyAccount?.asia_miles_number &&
    loyaltyAccount?.asia_miles_number !== '';

  const titleName =
    titleOptions?.find(
      (option) =>
        option.value.toLowerCase() === loyaltyAccount?.salutation?.toLowerCase()
    )?.label || '';

  const renderNameEditButton = (className = 'edit-button desktop-button') => (
    <Button
      type="link"
      icon={<img src={editIcon} className="edit-icon" />}
      onClick={() => setIsPersonalInfoEditVisible(true)}
      className={className}
    >
      {t('personalInfo.edit')}
    </Button>
  );

  const renderHomeAddressEditButton = (className = 'edit-button desktop-button') => (
    <Button
      type="link"
      icon={<img src={editIcon} className="edit-icon" />}
      onClick={() => setIsHomeAddressEditVisible(true)}
      className={className}
    >
      {t('personalInfo.edit')}
    </Button>
  );


  const renderEmailButton = (className = 'add-button desktop-button') => (
    <Button
      type="link"
      icon={
        hasEmail ? (
          <img src={editIcon} className="edit-icon" />
        ) : (
          <img src={smallPlusIcon} className="plus-icon" />
        )
      }
      onClick={() => handleAdd('email')}
      className={className}
    >
      {hasEmail ? t('personalInfo.editEmail') : t('personalInfo.addEmail')}
    </Button>
  );

  const renderAsiaMilesButton = (className = 'add-button desktop-button') => (
    <Button
      type="link"
      icon={
        hasAsiaMilesNo ? (
          <img src={editIcon} className="edit-icon" />
        ) : (
          <img src={smallPlusIcon} className="plus-icon" />
        )
      }
      onClick={() => handleAdd('asiaMiles')}
      className={className}
    >
      {hasAsiaMilesNo
        ? t('personalInfo.editAsiaMilesNo')
        : t('personalInfo.addAsiaMilesNo')}
    </Button>
  );

  const handleAdd = (item: string) => {
    // console.log(`Add ${item}`);
    if (item === 'email') {
      setIsEmailEditVisible(true);
    } else if (item === 'asiaMiles') {
      setIsAsiaMilesEditVisible(true);
    }
  };

  const handleDrawerOpen = (
    drawerType: 'personalInfo' | 'email' | 'asiaMiles' | 'newAddress'
  ) => {
    if (drawerType === 'personalInfo') {
      setIsPersonalInfoEditVisible(true);
    } else if (drawerType === 'email') {
      setIsEmailEditVisible(true);
    } else if (drawerType === 'asiaMiles') {
      setIsAsiaMilesEditVisible(true);
    } else if (drawerType === 'newAddress') {
      setIsNewAddressVisible(true);
    }
  };

  const handleDrawerClose = (
    drawerType: 'personalInfo' | 'email' | 'asiaMiles' | 'newAddress'
  ) => {
    if (drawerType === 'personalInfo') {
      setIsPersonalInfoEditVisible(false);
    } else if (drawerType === 'email') {
      setIsEmailEditVisible(false);
    } else if (drawerType === 'asiaMiles') {
      setIsAsiaMilesEditVisible(false);
    } else if (drawerType === 'newAddress') {
      setIsNewAddressVisible(false);
    }
  };

  const handleDrawerSave = async (
    drawerType:
      | 'personalInfo'
      | 'email'
      | 'asiaMiles'
      | 'homeAddressEdit'
      | 'newAddress'
      | 'addressEdit'
  ) => {
    // console.log(`Save ${drawerType}`);
    if (drawerType === 'personalInfo') {
      setIsPersonalInfoEditVisible(false);
      onSaveCallback?.();
    } else if (drawerType === 'email') {
      setIsEmailEditVisible(false);
      onSaveCallback?.();
    } else if (drawerType === 'asiaMiles') {
      setIsAsiaMilesEditVisible(false);
      onSaveCallback?.();
    } else if (drawerType === 'homeAddressEdit') {
      setIsHomeAddressEditVisible(false);
      onSaveCallback?.();
    } else if (drawerType === 'addressEdit') {
      setIsAddressEditVisible(false);
      try {
        refreshAddress();
      } catch (error) {
        message.error(t('fetchAddressError'));
      }
    } else if (drawerType === 'newAddress') {
      setIsNewAddressVisible(false);
      try {
        refreshAddress();
      } catch (error) {
        message.error(t('fetchAddressError'));
      }
    }

    message.success(tCommon('message.saveSuccess'));
  };

  const handleAddressEdit = (mode: AddressEditMode) => {
    // console.log(`Address edit mode: ${mode}`);
    setEditAddressMode(mode);
    setIsAddressEditVisible(true);
  };

  const refreshAddress = useCallback(async () => {
    // Refresh addresses list
    const addressDataList = await fetchAddress('/customer', {
      juicyAppUniqueId: loyaltyAccount?.unique_id ?? '',
      lang: lang,
    });
    setAddresses(addressDataList);
  }, [lang, loyaltyAccount?.unique_id, session?._tk]);

  const handleAddressMenuClick = (key: string) => {
    setIsAddressDropdownVisible(false);

    switch (key) {
    case 'reselect':
      handleAddressEdit('select-default');
      break;
    case 'remove':
      handleAddressEdit('delete');
      break;
    case 'add':
      handleDrawerOpen('newAddress');
      break;
    default:
      break;
    }
  };

  useEffect(() => {
    const initData = async () => {
      try {
        // Fetch addresses
        refreshAddress();

        // Fetch titles
        const titleData = await fetchTitle('/common', lang);
        if (titleData && typeof titleData === 'object') {
          const options = convertToOptions(titleData);
          setTitleOptions(options);
        }
      } catch (err) {
        console.error('Failed to obtain data', err);
      }
    };
    initData();
  }, [lang, refreshAddress]);

  // Address dropdown menu items
  const isDefaultAddress = true; // This would come from props or state in real implementation
  const addressMenuItems = [
    {
      key: 'reselect',
      label: (
        <div
          className={`profile-address-menu-item ${isDefaultAddress ? 'selected' : ''
          }`}
        >
          {t('addressEdit.reselectDefault')}
        </div>
      ),
      disabled: addresses.length <= 0,
    },
    {
      key: 'remove',
      label: (
        <div className="profile-address-menu-item">
          {t('addressEdit.remove')}
        </div>
      ),
      disabled: addresses.length <= 0,
    },
    {
      key: 'add',
      label: (
        <div className="profile-address-menu-item disabled">
          {t('addressEdit.addAddress')}
        </div>
      ),
      disabled: addresses.length >= 3,
    },
  ];

  // Render address dropdown
  const renderAddressDropdown = (showInXs: boolean) => {
    const isOpen = showInXs
      ? isMobileAddressDropdownVisible
      : isAddressDropdownVisible;
    const setIsOpen = showInXs
      ? setIsMobileAddressDropdownVisible
      : setIsAddressDropdownVisible;

    return (
      <Dropdown
        menu={{
          items: addressMenuItems,
          onClick: ({ key }) => handleAddressMenuClick(key),
        }}
        trigger={['click']}
        open={isOpen}
        onOpenChange={setIsOpen}
        placement="bottomRight"
        overlayClassName="profile-address-edit-dropdown"
      >
        <Button
          type="link"
          icon={<img src={editIcon} className="edit-icon" />}
          className={`edit-address-button ${showInXs ? 'mobile-button' : 'desktop-button'
          }`}
        >
          {t('addressEdit.editAddress')}
        </Button>
      </Dropdown>
    );
  };

  return (
    <>
      {/* Personal Information Section */}
      <div className="info-section">
        <div className="section-title">{t('personalInfo.title')}</div>

        {/* Full Name */}
        <div className="info-item">
          <div className="info-header">
            <div>{t('personalInfo.fullName')}</div>
            {renderNameEditButton('edit-button desktop-button')}
          </div>
          <div className="info-value" >
            {
              isEnglish
                ?titleName + ' ' + (loyaltyAccount?.first_name || '') + ' ' + (loyaltyAccount?.last_name || '')
                :(loyaltyAccount?.first_name || '') + ' ' + (loyaltyAccount?.last_name || '') + ' ' + titleName
            }
          </div>
        </div>

        {/* Birthday */}
        <div className="info-item">
          <div className="info-header">
            <div>{t('personalInfo.birthday')}</div>
          </div>
          <div className="info-value">
            {loyaltyAccount?.birth_month
              ? `${getMonthNameByNumber(loyaltyAccount.birth_month)} ****`
              : ''}
          </div>
        </div>

        {/* Mobile button - appears below content on mobile */}
        <div className="edit-name-button-container">
          {renderNameEditButton('edit-button mobile-button')}
        </div>

        <Divider className="divider" />

        {/* Email */}
        <div className="info-item">
          <div className="info-header">
            <div>{t('personalInfo.email')}</div>
            {renderEmailButton('add-button desktop-button')}
          </div>
          {hasEmail ? (
            <div className="info-value">{maskEmail(loyaltyAccount?.email)}</div>
          ) : (
            <div className="info-placeholder">
              {t('personalInfo.emailPlaceholder')}
            </div>
          )}
          {/* Mobile button - appears below content on mobile */}
          {renderEmailButton('add-button mobile-button')}
        </div>

        <Divider className="divider" />

        <div className="info-item">
          <div className="info-header">
            <div>{t('personalInfo.homeAddress')}</div>
            {renderHomeAddressEditButton('edit-button desktop-button')}
          </div>
          <div className="address-content">
            <Row className="address-cell" gutter={[12, 12]}>
              <Col xs={24} sm={8}>
                <div className="address-label">
                  {t('personalInfo.district')}
                </div>
                <div>
                  {isEnglish
                    ? loyaltyAccount?.district_en
                    : loyaltyAccount?.district_tc}{' '}
                </div>
              </Col>
              <Col xs={24} sm={16}>
                <div className="address-label">
                  {t('personalInfo.address')}{' '}
                </div>
                <div className="address-text">{loyaltyAccount?.address} </div>
              </Col>
            </Row>
          </div>

          {/* Mobile button - appears below content on mobile */}
          {renderHomeAddressEditButton('edit-button mobile-button')}
        </div>

        <Divider className="divider" />

        {/* Address */}
        <div className="info-item">
          <div className="info-header">
            <div>{t('personalInfo.pickupAddress')}</div>
            <div className="address-actions">
              {renderAddressDropdown(false)}
            </div>
          </div>
          <div className="address-content">
            {addresses.map((address) => (
              <Row
                key={address.addressID}
                className="address-cell"
                gutter={[12, 12]}
              >
                <Col xs={24} sm={8}>
                  <div className="address-label">
                    {t('personalInfo.district')}
                  </div>
                  <div>
                    {isEnglish
                      ? address.lodNamEn || address.lodNamTc || ''
                      : address.lodNamTc || address.lodNamEn || ''}
                  </div>
                </Col>
                <Col xs={24} sm={16}>
                  <div className="address-label">
                    {t('personalInfo.address')}{' '}
                    {address.seq === 1 && (
                      // <span className="default-tag">
                      //   {t('personalInfo.default')}
                      // </span>
                      <Tag className="default-tag">
                        {t('personalInfo.default')}
                      </Tag>
                    )}
                  </div>
                  <div className="address-text">{address.address} </div>
                </Col>
              </Row>
            ))}
          </div>
          {/* Mobile button - appears below content on mobile */}
          <div className="mobile-address-actions">
            {renderAddressDropdown(true)}
          </div>
        </div>

        <Divider className="divider" />

        {/* Asia Miles */}
        <div className="info-item">
          <div className="info-header">
            <div>{t('personalInfo.earnAsiaMiles')}</div>
            {renderAsiaMilesButton('add-button desktop-button')}
          </div>
          <div>{loyaltyAccount?.asia_miles_number}</div>
          {/* Mobile button - appears below content on mobile */}
          {renderAsiaMilesButton('add-button mobile-button')}
        </div>
      </div>

      {/* Personal Info Edit Drawer */}
      <PersonalInfoEdit
        visible={isPersonalInfoEditVisible}
        onClose={() => handleDrawerClose('personalInfo')}
        onSave={() => handleDrawerSave('personalInfo')}
        loyaltyAccount={loyaltyAccount}
        titleOptions={titleOptions}
      />

      {/* Email Edit Drawer */}
      <EmailEdit
        visible={isEmailEditVisible}
        onClose={() => handleDrawerClose('email')}
        // onSave={() => handleDrawerSave('email')}
        loyaltyAccount={loyaltyAccount}
      />

      {/* Asia Miles Edit Drawer */}
      <AsiaMilesEdit
        visible={isAsiaMilesEditVisible}
        onClose={() => handleDrawerClose('asiaMiles')}
        onSave={() => handleDrawerSave('asiaMiles')}
        loyaltyAccount={loyaltyAccount}
      />

      {/* Home Address Edit Drawer */}
      <HomeAddressEdit
        visible={isHomeAddressEditVisible}
        onClose={() => setIsHomeAddressEditVisible(false)}
        loyaltyAccount={loyaltyAccount}
        onSave={() => handleDrawerSave('homeAddressEdit')}
      />

      {/* Address Edit Drawer */}
      <AddressEdit
        visible={isAddressEditVisible}
        onClose={() => setIsAddressEditVisible(false)}
        mode={editAddressMode}
        addresses={addresses}
        onSuccess={() => handleDrawerSave('addressEdit')}
      />

      {/* New Address Drawer */}
      <DrawerFormContainer
        title={''}
        visible={isNewAddressVisible}
        onClose={() => handleDrawerClose('newAddress')}
      >
        <NewAddress
          onClose={() => handleDrawerClose('newAddress')}
          onSave={() => handleDrawerSave('newAddress')}
          needTransitionAnimation={false}
          title={t('addressEdit.addAddressTitle')}
        />
      </DrawerFormContainer>
    </>
  );
};

export default PersonalInfoSection;